import { useTranslation } from '@bcba/react-hooks';
import { DigitalDossierRepresentantInformationSerializer } from '@bcba/serializers';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { useLegalRepresentativesForm } from './useLegalRepresentativesForm';
import {
  Alert,
  Badge,
  Card,
  Divider,
  DropzoneReadonly,
  EmptyState,
  FiFileText,
  FiSearch,
  FormProvider,
  Grid,
  GridItem,
  ReadOnlyField,
  Stack,
  TextField,
} from '@agrotoken-tech/ui';
import { FormSection } from '../components/FormSection';
import { validationStatusBadgeColors } from '@bcba/frontend-utils';
import { downloadDocument } from '../../../../utils/downloadDocument.utils';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { VALIDATION_STATUSES, YES_NO_OPTIONS } from '@bcba/types';
import { ReadOnlyYesNoRadio } from '../../components/ReadOnlyYesNoRadio';

type Props = {
  data: DigitalDossierRepresentantInformationSerializer[];
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<
      DigitalDossierRepresentantInformationSerializer[],
      Error
    >
  >;
};

export const LegalRepresentativesForm = ({ data, refetch }: Props) => {
  const { t } = useTranslation();

  const {
    formMethods,
    handleSubmit,
    onSubmit,
    status,
    validationMessage,
    isPendingChangeRequest,
    actionButtons,
    isFormReadOnly,
  } = useLegalRepresentativesForm(data, refetch);
  const { register, watch } = formMethods;

  const toBeCompletedByBolsa = t('common.toBeCompletedByBolsa');

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormSection
          title={t('common.legalRepresentatives')}
          badge={
            status !== VALIDATION_STATUSES.INCOMPLETE && (
              <Badge colorScheme={validationStatusBadgeColors[status]}>
                {t(`validationStatus.${status}`)}
              </Badge>
            )
          }
        >
          {data.length ? (
            <Stack gap={8} divider={<Divider />}>
              {isPendingChangeRequest && (
                <Alert
                  title={t('explorer.changeRequest.alert.title')}
                  description={validationMessage}
                  variant="error"
                />
              )}
              {data.map((repInfo, index) => {
                const prefix = `legalRepresentants.${index}` as const;
                const {
                  identityDocumentType,
                  position,
                  productCaps,
                  currencyCaps,
                } = repInfo ?? {};
                const docInDb = repInfo?.representantDocuments[0]; // TODO: we should receive the document in the serializer, not an array
                const docExpirationDate = docInDb?.document.expirationDate;
                const hasExchangeCap = Boolean(
                  repInfo?.powerOfAttorneyExchanges?.length,
                );
                const emailReadonlyValue = watch(`${prefix}.email`);
                const hasProductCap = Boolean(repInfo?.productCaps.length);
                const hasCurrencyCap = Boolean(repInfo?.currencyCaps.length);
                return (
                  <FormSection
                    key={repInfo.uuid}
                    title={t(
                      'digitalDossier.updateLegalRepresentantivesInfo.representativeSection.title',
                    ).replace('{{num}}', String(index + 1))}
                  >
                    <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
                      <input
                        type="hidden"
                        style={{ display: 'none' }}
                        {...register(`${prefix}.uuid`)}
                        value={repInfo?.uuid}
                      />
                      <ReadOnlyField
                        label={t('common.name')}
                        value={repInfo?.firstName ?? toBeCompletedByBolsa}
                      />
                      <ReadOnlyField
                        label={t('common.lastName')}
                        value={repInfo?.lastName ?? toBeCompletedByBolsa}
                      />
                      <ReadOnlyField
                        label={t('common.documentType')}
                        value={
                          identityDocumentType
                            ? t(`identityDocumentType.${identityDocumentType}`)
                            : toBeCompletedByBolsa
                        }
                      />
                      <ReadOnlyField
                        label={t('common.document')}
                        value={
                          repInfo?.identityDocumentId ?? toBeCompletedByBolsa
                        }
                      />
                      <TextField
                        name={`${prefix}.email`}
                        label={t('common.email')}
                        errorMessageFormatter={t}
                        isReadOnly={isFormReadOnly}
                        readOnlyValue={emailReadonlyValue}
                      />
                      <ReadOnlyField
                        label={t('common.position')}
                        value={
                          position
                            ? t(`representativePosition.${position}`)
                            : toBeCompletedByBolsa
                        }
                      />
                      <ReadOnlyField
                        label={t('common.startDateOfTerm')}
                        value={
                          repInfo?.startOfTerm
                            ? formatDateToShortFormat(
                                unixTimestampToDate(repInfo.startOfTerm),
                              )
                            : toBeCompletedByBolsa
                        }
                      />
                      <ReadOnlyField
                        label={t('common.endDateOfTerm')}
                        value={
                          repInfo?.endOfTerm
                            ? formatDateToShortFormat(
                                unixTimestampToDate(repInfo.endOfTerm),
                              )
                            : toBeCompletedByBolsa
                        }
                      />
                      {docInDb && (
                        <GridItem colSpan={{ base: 1, md: 2 }}>
                          <DropzoneReadonly
                            fileName={docInDb.document.originalFileName}
                            label={t(
                              'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.idDocument',
                            )}
                            onClick={() =>
                              downloadDocument(docInDb.document.uuid)
                            }
                            leftIcon={FiFileText}
                          />
                        </GridItem>
                      )}
                      <ReadOnlyField
                        label={t('common.documentIdentifier')}
                        value={
                          docInDb?.document.identifier ?? toBeCompletedByBolsa
                        }
                      />
                      <ReadOnlyField
                        label={t('common.dueDate')}
                        value={
                          docExpirationDate
                            ? formatDateToShortFormat(
                                unixTimestampToDate(docExpirationDate),
                              )
                            : toBeCompletedByBolsa
                        }
                      />
                      <ReadOnlyField
                        label={t(
                          'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.idOfTheAuthorizedLegalRepresentationDocument',
                        )}
                        value={
                          docInDb?.bcAuthorizedDocumentIdentifier ??
                          toBeCompletedByBolsa
                        }
                      />
                      <GridItem />
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <Card>
                          <FormSection
                            title={t(
                              'digitalDossier.legalRepresentatives.representativeCaps',
                            )}
                          >
                            <Stack gap={8} divider={<Divider />}>
                              <Grid
                                templateColumns={{ md: 'repeat(2, 1fr)' }}
                                gap={6}
                              >
                                <ReadOnlyYesNoRadio
                                  label={t('common.powerByBolsa')}
                                  defaultValue={
                                    hasExchangeCap
                                      ? YES_NO_OPTIONS.YES
                                      : YES_NO_OPTIONS.NO
                                  }
                                />
                                {hasExchangeCap && (
                                  <ReadOnlyField
                                    label={'Bolsa'}
                                    value={repInfo.powerOfAttorneyExchanges
                                      .map((e) =>
                                        t(`powerOfAttorneyExchange.${e}`),
                                      )
                                      .join(', ')}
                                  />
                                )}
                              </Grid>
                              <Grid
                                templateColumns={{ md: 'repeat(2, 1fr)' }}
                                gap={6}
                              >
                                <GridItem colSpan={{ base: 1, md: 2 }}>
                                  <ReadOnlyYesNoRadio
                                    label={t(
                                      'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.hasProductCap',
                                    )}
                                    defaultValue={
                                      hasProductCap
                                        ? YES_NO_OPTIONS.YES
                                        : YES_NO_OPTIONS.NO
                                    }
                                  />
                                </GridItem>
                                {hasProductCap &&
                                  productCaps?.map(({ product, cap }) => (
                                    <>
                                      <ReadOnlyField
                                        label={t('common.product')}
                                        value={t(`product.${product}`)}
                                      />
                                      <ReadOnlyField
                                        label={t('common.tons')}
                                        value={String(cap)}
                                      />
                                    </>
                                  ))}
                              </Grid>
                              <Grid
                                templateColumns={{ md: 'repeat(2, 1fr)' }}
                                gap={6}
                              >
                                <GridItem colSpan={{ base: 1, md: 2 }}>
                                  <ReadOnlyYesNoRadio
                                    label={t(
                                      'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.hasCurrencyCap',
                                    )}
                                    defaultValue={
                                      hasCurrencyCap
                                        ? YES_NO_OPTIONS.YES
                                        : YES_NO_OPTIONS.NO
                                    }
                                  />
                                </GridItem>
                                {hasCurrencyCap &&
                                  currencyCaps?.map(({ currency, cap }) => (
                                    <>
                                      <ReadOnlyField
                                        label={t('common.currency')}
                                        value={t(`currency.${currency}`)}
                                      />
                                      <ReadOnlyField
                                        label={t('common.amount')}
                                        value={String(cap)}
                                      />
                                    </>
                                  ))}
                              </Grid>
                            </Stack>
                          </FormSection>
                        </Card>
                      </GridItem>
                    </Grid>
                  </FormSection>
                );
              })}
              {actionButtons}
            </Stack>
          ) : (
            <EmptyState
              title={t(
                'explorer.digitalDossier.updateLegalRepresentantivesInfo.emptyState.title',
              )}
              description={t(
                'explorer.digitalDossier.updateLegalRepresentantivesInfo.emptyState.description',
              )}
              size="md"
              icon={FiSearch}
            />
          )}
        </FormSection>
      </form>
    </FormProvider>
  );
};
